import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  Home,
  Menu,
  X,
} from "lucide-react";
import logo from "../../Assets/Images/nav_logo.png";
import logoDarkTheme from "../../Assets/Images/Cook-And-Chef-Final-logo-darktheme.webp";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import {
  getPublicNavClassName,
  usePublicNavScroll,
} from "../../hooks/usePublicNavScroll";
import RoleSelectorModal from "./RoleSelectorModal";
import styles from "./HomeLanding.module.css";

const INSTITUTE_URL = "https://institute.cookandchef.in";

const NAV_LINKS = [
  {
    label: "For Restaurants (HoReCa)",
    to: "/employer-registration?type=business",
    icon: Building2,
  },
  {
    label: "For Households",
    to: "/employer-registration?type=home",
    icon: Home,
  },
];

export default function HomeNavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);
  const isScrolled = usePublicNavScroll();

  useLockBodyScroll(menuOpen);
  const closeMenu = () => setMenuOpen(false);

  const openRoleSelector = () => {
    closeMenu();
    setRoleSelectorOpen(true);
  };

  const renderDesktopLink = (item) => {
    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.navLink}
          onClick={closeMenu}
        >
          {item.label}
        </a>
      );
    }
    return (
      <Link
        key={item.label}
        to={item.to}
        className={styles.navLink}
        onClick={closeMenu}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header
      className={getPublicNavClassName(styles, { menuOpen, isScrolled })}
    >
      <div className={styles.navInner}>
        <Link to="/" className={styles.logoLink} onClick={closeMenu}>
          <img
            src={logo}
            alt="CookandChef"
            className={styles.logo}
            width="240"
            height="100"
          />
        </Link>

        <div className={styles.navCenter}>
          <nav className={styles.navLinks} aria-label="Main navigation">
            {NAV_LINKS.map(renderDesktopLink)}
          </nav>
        </div>

        <div className={styles.navActions}>
          <Link to="/login" className={styles.loginLink} onClick={closeMenu}>
            Login
          </Link>
          <button
            type="button"
            className={styles.getStartedBtn}
            onClick={openRoleSelector}
          >
            Get Started
          </button>
        </div>

        <button
          type="button"
          className={styles.menuToggle}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div className={styles.mobileMenuOverlay}>
          <button
            type="button"
            className={styles.mobileMenuBackdrop}
            aria-label="Close menu"
            onClick={closeMenu}
          />

          <div
            className={styles.mobileMenuCard}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className={styles.mobileMenuWelcome}>
              <div className={styles.mobileMenuWelcomeTop}>
                <Link to="/" className={styles.mobileMenuLogoLink} onClick={closeMenu}>
                  <img
                    src={logoDarkTheme}
                    alt="CookandChef"
                    className={styles.mobileMenuLogo}
                  />
                </Link>
                <button
                  type="button"
                  className={styles.mobileMenuClose}
                  aria-label="Close menu"
                  onClick={closeMenu}
                >
                  <X size={20} />
                </button>
              </div>

              <p className={styles.mobileMenuWelcomeTitle}>Welcome</p>
              <p className={styles.mobileMenuWelcomeSub}>
                Login or get started to continue
              </p>

              <div className={styles.mobileMenuCtaRow}>
                <Link
                  to="/login"
                  className={styles.mobileMenuLogin}
                  onClick={closeMenu}
                >
                  Login
                </Link>
                <button
                  type="button"
                  className={styles.mobileMenuEmployerBtn}
                  onClick={openRoleSelector}
                >
                  Get Started
                </button>
              </div>
            </div>

            <nav className={styles.mobileMenuNav} aria-label="Mobile navigation">
              {NAV_LINKS.map(({ label, to, icon: Icon }) => (
                <Link
                  key={label}
                  to={to}
                  className={styles.mobileMenuLink}
                  onClick={closeMenu}
                >
                  <span className={styles.mobileMenuLinkIcon} aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className={styles.mobileMenuLinkLabel}>{label}</span>
                  <ChevronRight
                    size={18}
                    className={styles.mobileMenuLinkChevron}
                    aria-hidden="true"
                  />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      <RoleSelectorModal
        open={roleSelectorOpen}
        onClose={() => setRoleSelectorOpen(false)}
      />
    </header>
  );
}

export { INSTITUTE_URL };
